var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { registerForIntl, provideIntlService, registerForLocalization, provideLocalizationService } from '@progress/kendo-react-intl';
import { guid, classNames, FloatingLabel, dispatchEvent } from '@progress/kendo-react-common';
import { messages, numericIncreaseValue, numericDecreaseValue } from './../messages';
var VALIDATION_MESSAGE = 'Please enter a valid value!';
// tslint:enable:max-line-length
var NumericTextBox = /** @class */ (function (_super) {
    __extends(NumericTextBox, _super);
    function NumericTextBox(props) {
        var _this = _super.call(this, props) || this;
        _this.valueDuringOnChange = undefined;
        _this.textBeforeInput = '';
        _this._inputId = guid();
        _this.setValidity = function () {
            if (_this.element && _this.element.setCustomValidity) {
                _this.element.setCustomValidity(_this.validity.valid
                    ? ''
                    : _this.props.validationMessage || VALIDATION_MESSAGE);
            }
        };
        _this.elementChange = _this.elementChange.bind(_this);
        _this.increase = _this.increase.bind(_this);
        _this.decrease = _this.decrease.bind(_this);
        _this.wheel = _this.wheel.bind(_this);
        _this.keyDown = _this.keyDown.bind(_this);
        var initialState = {
            value: null,
            looselyFormattedText: null,
            selectionStart: 0,
            selectionEnd: 0,
            decimalSelect: false
        };
        if (_this.props.value !== undefined) {
            initialState.value = _this.props.value;
        }
        else if (_this.props.defaultValue !== undefined) {
            initialState.value = _this.props.defaultValue;
        }
        _this.state = initialState;
        return _this;
    }
    Object.defineProperty(NumericTextBox.prototype, "name", {
        /**
         * Gets the `name` property of the NumericTextBox.
         */
        get: function () {
            return this.props.name;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NumericTextBox.prototype, "value", {
        /**
         * Gets the value of the NumericTextBox.
         */
        get: function () {
            if (this.valueDuringOnChange !== undefined) {
                return this.valueDuringOnChange;
            }
            else {
                return this.state.value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NumericTextBox.prototype, "validity", {
        /**
         * Represents the validity state into which the NumericTextBox is set.
         */
        get: function () {
            // The NumericTextBox currently autocorrect its' value,
            // so the only invalid state is if it's required and
            // the value is null!
            var customError = this.props.validationMessage !== undefined;
            var isValid = (!this.required || (this.value !== null));
            var valid = this.props.valid !== undefined ? this.props.valid : isValid;
            return {
                customError: customError,
                valid: valid,
                valueMissing: this.value === null
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NumericTextBox.prototype, "validityStyles", {
        /**
         * @hidden
         */
        get: function () {
            return this.props.validityStyles !== undefined
                ? this.props.validityStyles
                : NumericTextBox.defaultProps.validityStyles;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NumericTextBox.prototype, "required", {
        /**
         * @hidden
         */
        get: function () {
            return this.props.required !== undefined
                ? this.props.required
                : NumericTextBox.defaultProps.required;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NumericTextBox.prototype, "element", {
        /**
         * Gets the element of the NumericTextBox.
         *
         * @return - An `HTMLInputElement`.
         *
         * @example
         * ```jsx
         * class App extends React.Component {
         *     constructor(props) {
         *         super(props);
         *     }
         *     element = null;
         *     render() {
         *         return (
         *             <div>
         *                 <NumericTextBox
         *                     ref={(numericTextBox) =>
         *                         this.element = numericTextBox ? numericTextBox.element : null}
         *                 />
         *                 <button onClick={() => console.log(this.element)}>console.log the element</button>
         *             </div>
         *         );
         *     }
         * }
         *
         * ReactDOM.render(
         *     <App />,
         *     document.getElementsByTagName('my-app')[0]
         * );
         * ```
         */
        get: function () {
            return this._element;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    NumericTextBox.prototype.componentWillReceiveProps = function (nextProps) {
        if (nextProps.value !== undefined && this.state.value !== nextProps.value) {
            this.setState({
                value: nextProps.value
            });
        }
    };
    /**
     * @hidden
     */
    NumericTextBox.prototype.componentDidMount = function () {
        if (this.element) {
            this.textBeforeInput = this.element.value;
        }
        this.setValidity();
    };
    /**
     * @hidden
     */
    NumericTextBox.prototype.componentDidUpdate = function (_, prevState) {
        if (!(document && document.activeElement !== this.element || !this.element) &&
            this.state.looselyFormattedText !== null) {
            // An IE related issue.
            // When having multiple inputs and if selection is applied, the caret moves to the latest changed input.
            // So, skip the application of the selection if the input is not focused.
            if (this.element.value !== this.state.looselyFormattedText && this.state.value === prevState.value) {
                // culture is changed, typing was not valid or was ranged.
                this.element.selectionStart = this.state.selectionStart;
                this.element.selectionEnd = this.state.selectionEnd;
            }
            else {
                var position = void 0;
                var indexOfDecimal = this.state.looselyFormattedText.indexOf(this.symbols.decimal);
                if (indexOfDecimal >= 0 && indexOfDecimal < this.state.selectionStart) {
                    position = this.state.selectionEnd;
                }
                else {
                    position = this.state.selectionEnd -
                        (this.state.looselyFormattedText.length - this.element.value.length);
                }
                var indexOfNewDecimal = this.element.value.indexOf(this.symbols.decimal);
                if (this.state.decimalSelect || (this.state.value === null && indexOfNewDecimal >= 0) ||
                    (indexOfDecimal === -1 && indexOfNewDecimal >= 0)) {
                    if (this.state.selectionStart < indexOfNewDecimal + 2 && indexOfDecimal >= 0) {
                        position = indexOfNewDecimal + 1;
                    }
                    else {
                        position = indexOfNewDecimal;
                    }
                }
                if (!this.state.decimalSelect && (this.state.value === prevState.value)) {
                    position = this.state.selectionEnd -
                        (this.state.looselyFormattedText.length - this.element.value.length);
                }
                if (this.state.looselyFormattedText[0] === this.symbols.decimal
                    && prevState.looselyFormattedText === this.symbols.decimal) {
                    position = this.state.value !== undefined ? this.formatNumber(this.state.value).length : 0;
                }
                this.element.selectionStart = this.element.selectionEnd = position;
            }
        }
        if (this.element) {
            this.textBeforeInput = this.element.value;
        }
        this.setValidity();
    };
    Object.defineProperty(NumericTextBox.prototype, "valueAsLocalString", {
        /**
         * @hidden
         */
        get: function () {
            this.intl = provideIntlService(this);
            this.symbols = this.intl.numberSymbols();
            if (this.state.looselyFormattedText !== null &&
                !this.state.decimalSelect &&
                this.parseNumber(this.state.looselyFormattedText) === this.state.value &&
                this.formatNumber(this.state.value).length < this.state.looselyFormattedText.length &&
                this.state.looselyFormattedText.indexOf(this.formatNumber(this.state.value)) === 0) {
                // the user is currently typing, do not interrupt them
                return this.state.looselyFormattedText;
            }
            return this.formatNumber(this.state.value);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    NumericTextBox.prototype.render = function () {
        var _this = this;
        var inputId = this.props.id || this._inputId;
        var localizationService = provideLocalizationService(this);
        var isValid = !this.validityStyles || this.validity.valid;
        var numerictextbox = (React.createElement("span", { dir: this.props.dir, style: !this.props.label
                ? { width: this.props.width }
                : undefined, className: 'k-widget k-numerictextbox'
                + (this.props.className ? ' ' + this.props.className : '') },
            React.createElement("span", { className: classNames('k-numeric-wrap', {
                    'k-state-disabled': this.props.disabled,
                    'k-state-invalid': !isValid
                }), ref: function (elementWrapper) { _this.elementWrapper = elementWrapper; } },
                React.createElement("input", { tabIndex: this.props.tabIndex, disabled: this.props.disabled, title: this.props.title, "aria-valuemin": this.props.min, "aria-valuemax": this.props.max, placeholder: this.props.placeholder, type: this.props.inputType || 'tel', spellCheck: false, autoComplete: "off", autoCorrect: "off", className: "k-input k-formatted-value", id: inputId, value: this.valueAsLocalString, "aria-valuenow": (this.state.value !== null) ? this.state.value : undefined, name: this.props.name, onWheel: this.wheel, onKeyDown: this.keyDown, onChange: this.elementChange, ref: function (input) { return _this._element = input; } }),
                this.props.children,
                this.props.spinners &&
                    React.createElement("span", { className: "k-select", onMouseDown: function (e) {
                            if (document && _this._element) {
                                e.preventDefault();
                                if (document.activeElement !== _this._element) {
                                    _this._element.focus();
                                }
                            }
                        } },
                        React.createElement("span", { className: "k-link k-link-increase", "aria-label": localizationService
                                .toLanguageString(numericIncreaseValue, messages[numericIncreaseValue]), title: localizationService
                                .toLanguageString(numericIncreaseValue, messages[numericIncreaseValue]), onClick: this.increase },
                            React.createElement("span", { className: "k-icon k-i-arrow-n" })),
                        React.createElement("span", { className: "k-link k-link-decrease", "aria-label": localizationService
                                .toLanguageString(numericDecreaseValue, messages[numericDecreaseValue]), title: localizationService
                                .toLanguageString(numericDecreaseValue, messages[numericDecreaseValue]), onClick: this.decrease },
                            React.createElement("span", { className: "k-icon k-i-arrow-s" }))))));
        return this.props.label
            ? (React.createElement(FloatingLabel, { id: inputId, value: this.valueAsLocalString, valid: isValid, placeholder: this.props.placeholder, label: this.props.label, children: numerictextbox, style: { width: this.props.width } }))
            : numerictextbox;
    };
    NumericTextBox.prototype.formatNumber = function (value) {
        if (value == null) {
            return '';
        }
        return this.intl.formatNumber(value, this.props.format).toString();
        // the second argument actually supports null/undefined
    };
    NumericTextBox.prototype.parseNumber = function (text) {
        return this.intl.parseNumber(text, this.props.format);
    };
    NumericTextBox.prototype.elementChange = function (event) {
        if (!this.element) {
            return;
        }
        var element = this.element;
        var newState = {
            value: this.state.value,
            looselyFormattedText: element.value,
            selectionStart: this.element.selectionStart,
            selectionEnd: this.element.selectionEnd,
            decimalSelect: false
        };
        var text = element.value; // do NOT use 'event.target.value', always use 'element.value'
        var minusSign = this.symbols.minusSign;
        if (text === minusSign) {
            newState.value = null;
            this.triggerChange(newState, event);
            return;
        }
        if (text.split(minusSign).length !== this.textBeforeInput.split(minusSign).length &&
            text.length === this.textBeforeInput.length + minusSign.length) {
            // positive/negative change
            newState.value = -this.parseNumber(this.textBeforeInput);
            this.triggerChange(newState, event);
            return;
        }
        var decimal = this.symbols.decimal;
        if (text.split(RegExp('\\' + decimal)).length > 2) {
            // prevent typing second decimal separator
            newState.decimalSelect = true;
            this.triggerChange(newState, event);
            return;
        }
        if (text.length > 1 && text.indexOf(decimal) === -1 && this.textBeforeInput.indexOf(decimal) >= 0 &&
            text.length + decimal.length === this.textBeforeInput.length) {
            // prevent deleting of decimal separator on its own
            newState.decimalSelect = true;
            this.triggerChange(newState, event);
            return;
        }
        var value = this.parseNumber(text);
        if (value === this.state.value && text.length > 0 && text[text.length - 1] === decimal) {
            if ((this.formatNumber(1.1) || '').toString().indexOf(decimal) > -1) {
                newState.looselyFormattedText = text;
                this.triggerChange(newState, event);
                return;
            }
        }
        var invalid = ((value === null || isNaN(value)) && text.length > 0 && text !== this.symbols.minusSign);
        var wrapper = this.elementWrapper;
        if ((text.length > this.textBeforeInput.length) &&
            (this.parseNumber(text) === this.parseNumber(text.slice(0, element.selectionStart)) &&
                (this.parseNumber(text.slice(element.selectionStart - 1)) === null ||
                    isNaN(this.parseNumber(text.slice(element.selectionStart - 1)))))) {
            invalid = true;
        }
        if (invalid) {
            // block typing of invalid characters
            if (wrapper && wrapper.className.indexOf("k-state-invalid") === -1) {
                wrapper.className += ' k-state-invalid';
                setTimeout(function () { wrapper.className = wrapper.className.replace(' k-state-invalid', ''); }, 50);
            }
            newState.looselyFormattedText = this.textBeforeInput;
            newState.selectionStart = newState.selectionEnd =
                element.selectionEnd + this.textBeforeInput.length - element.value.length;
            this.triggerChange(newState, event);
            return;
        }
        newState.value = value;
        this.triggerChange(newState, event);
    };
    NumericTextBox.prototype.nonInputActions = function (value, event) {
        var newState = {
            value: value,
            looselyFormattedText: null,
            selectionStart: 0,
            selectionEnd: 0,
            decimalSelect: false
        };
        this.triggerChange(newState, event);
    };
    NumericTextBox.prototype.triggerChange = function (newState, event) {
        if (this.props.disabled) {
            return;
        }
        // decimals ranging based on the format:
        this.valueDuringOnChange = this.ranged(this.parseNumber(this.formatNumber(this.ranged(newState.value))));
        // const value = newState.value;
        var shouldFireEvent = (this.valueDuringOnChange !== this.state.value);
        if (this.props.value !== undefined) {
            // controlled
            newState.value = this.props.value;
        }
        else {
            // uncontrolled
            newState.value = this.valueDuringOnChange;
        }
        this.setState(newState);
        if (shouldFireEvent) {
            dispatchEvent(this.props.onChange, event, this, { value: this.valueDuringOnChange });
        }
        this.valueDuringOnChange = undefined;
    };
    NumericTextBox.prototype.ranged = function (value) {
        if (value == null) {
            return value;
        }
        if (!(value > 1 || value < 1 || value === 1)) {
            // null, undefined or NaN
            return null;
        }
        if (this.props.max !== undefined && this.props.min !== undefined && this.props.max < this.props.min) {
            return null;
        }
        // TODO: This should be something improved like
        // while (value < max) { value *= 10 }
        // and the selection should be adjusted
        // to get good experience when typing values in ranges like [222,11111]
        if (this.props.max !== undefined && value > this.props.max) {
            value = this.props.max;
        }
        if (this.props.min !== undefined && value < this.props.min) {
            value = this.props.min;
        }
        return value;
    };
    NumericTextBox.prototype.increase = function (syntheticEvent) {
        this.nonInputActions((this.state.value || 0) + (this.props.step || 0), syntheticEvent);
    };
    NumericTextBox.prototype.decrease = function (syntheticEvent) {
        this.nonInputActions((this.state.value || 0) - (this.props.step || 0), syntheticEvent);
    };
    NumericTextBox.prototype.wheel = function (event) {
        if (!document || document.activeElement !== this.element || !this.element) {
            return;
        }
        if (event.nativeEvent.deltaY < 0) {
            event.preventDefault();
            this.increase(event);
        }
        if (event.nativeEvent.deltaY > 0) {
            event.preventDefault();
            this.decrease(event);
        }
    };
    NumericTextBox.prototype.keyDown = function (event) {
        switch (event.keyCode) {
            case 38:
                // arrow up
                this.increase(event);
                break;
            case 40:
                // arrow down
                this.decrease(event);
                break;
            case 110:
                // numpad decimal key
                var element = this.element;
                if (element) {
                    var text = element.value;
                    var selectionStart = element.selectionStart, selectionEnd = element.selectionEnd;
                    element.value = text.slice(0, selectionStart) + this.symbols.decimal + text.slice(selectionEnd);
                    element.selectionStart = element.selectionEnd = selectionStart + 1;
                    this.elementChange(event); // TODO: remove this call (use triggerChange directly with custom state)
                }
                break;
            default:
                return;
        }
        event.preventDefault();
    };
    /**
     * @hidden
     */
    NumericTextBox.propTypes = {
        value: PropTypes.number,
        defaultValue: PropTypes.number,
        step: PropTypes.number,
        format: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                style: PropTypes.oneOf(['decimal', 'currency', 'percent', 'scientific', 'accounting']),
                currency: PropTypes.string,
                currencyDisplay: PropTypes.oneOf(['symbol', 'code', 'name']),
                useGrouping: PropTypes.bool,
                minimumIntegerDigits: PropTypes.number,
                minimumFractionDigits: PropTypes.number,
                maximumFractionDigits: PropTypes.number
            })
        ]),
        width: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        tabIndex: PropTypes.number,
        title: PropTypes.string,
        placeholder: PropTypes.string,
        min: PropTypes.number,
        max: PropTypes.number,
        spinners: PropTypes.bool,
        disabled: PropTypes.bool,
        dir: PropTypes.string,
        name: PropTypes.string,
        label: PropTypes.string,
        validationMessage: PropTypes.string,
        required: PropTypes.bool,
        id: PropTypes.string,
        onChange: PropTypes.func
    };
    /**
     * @hidden
     */
    NumericTextBox.defaultProps = {
        spinners: true,
        disabled: false,
        onChange: function (_) { return; },
        step: 1,
        required: false,
        validityStyles: true
        // the rest of the properties are undefined by default
    };
    return NumericTextBox;
}(React.Component));
export { NumericTextBox };
registerForIntl(NumericTextBox);
registerForLocalization(NumericTextBox);
//# sourceMappingURL=NumericTextBox.js.map